/**
 * My Approvals — iter48.
 *
 * Every user with pending approvals sees them here. Tabs:
 *   • Pending (assigned to me)
 *   • My Requests (submitted by me)
 *   • History (my past decisions)
 *
 * Also includes a Generic Approval Request form so any user can
 * exercise the engine end-to-end before Phase 6 self-service flows land.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDateTimeFull } from "@/lib/helpers";
import {
  Card, CardHeader, CardTitle, CardContent, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ClipboardText, Clock, CheckCircle, XCircle, ArrowsClockwise,
  WarningCircle, HourglassMedium, Plus, PaperPlaneTilt,
  BellSimple, GitBranch, UserSwitch, Trash, CalendarBlank,
} from "@phosphor-icons/react";

const REQUEST_TYPE_LABEL = {
  leave_request: "Leave Request",
  attendance_correction: "Attendance Correction",
  profile_update: "Profile Update",
  generic: "Generic Approval Request",
};

const STATUS_BADGE = {
  pending:   { c: "bg-amber-100 text-amber-800 border-amber-200",     label: "Pending" },
  approved:  { c: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Approved" },
  rejected:  { c: "bg-rose-100 text-rose-800 border-rose-200",        label: "Rejected" },
  withdrawn: { c: "bg-slate-100 text-slate-700 border-slate-200",     label: "Withdrawn" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return fmtDateTimeFull(iso);
}

function slaState(req) {
  if (req.status !== "pending" || !req.sla_deadline_at) return null;
  const now = Date.now();
  const deadline = new Date(req.sla_deadline_at).getTime();
  const created = new Date(req.updated_at || req.created_at).getTime();
  const total = deadline - created;
  const remaining = deadline - now;
  const pct = total > 0 ? remaining / total : 1;
  if (remaining < 0) return { kind: "breached", label: "SLA breached", pct };
  if (pct < 0.2) return { kind: "warning", label: `${Math.max(0, Math.round(remaining / 3600000))}h left`, pct };
  return { kind: "ok", label: `${Math.max(0, Math.round(remaining / 3600000))}h left`, pct };
}


export default function MyApprovalsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [detailReq, setDetailReq] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey(k => k + 1);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="my-approvals-page">
      <div className="flex items-center gap-3 flex-wrap">
        <ClipboardText size={28} className="text-primary" weight="duotone" />
        <div>
          <h1 className="text-2xl font-display font-bold tracking-tight">My Approvals</h1>
          <p className="text-xs text-muted-foreground">
            Requests awaiting your action, plus your own submissions and decision history.
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={bump} data-testid="approvals-refresh"><ArrowsClockwise size={13} className="mr-1" /> Refresh</Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="approvals-new-generic"><Plus size={13} className="mr-1" /> New request</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 h-9">
          <TabsTrigger value="pending" data-testid="tab-pending"><BellSimple size={12} className="mr-1" /> Assigned to me</TabsTrigger>
          <TabsTrigger value="my" data-testid="tab-my"><ClipboardText size={12} className="mr-1" /> My requests</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><GitBranch size={12} className="mr-1" /> My history</TabsTrigger>
          <TabsTrigger value="delegations" data-testid="tab-delegations"><UserSwitch size={12} className="mr-1" /> Delegations</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-3">
          <PendingList key={`p-${refreshKey}`} onOpen={setDetailReq} />
        </TabsContent>
        <TabsContent value="my" className="mt-3">
          <MyRequestsList key={`m-${refreshKey}`} onOpen={setDetailReq} onWithdrawn={bump} />
        </TabsContent>
        <TabsContent value="history" className="mt-3">
          <HistoryList key={`h-${refreshKey}`} onOpen={setDetailReq} />
        </TabsContent>
        <TabsContent value="delegations" className="mt-3">
          <DelegationsPanel key={`d-${refreshKey}`} currentUserId={user?.id} />
        </TabsContent>
      </Tabs>

      <RequestDetailDialog
        req={detailReq}
        onClose={() => setDetailReq(null)}
        onActed={() => { setDetailReq(null); bump(); }}
        currentUserId={user?.id}
      />

      <GenericRequestDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmitted={() => { setCreateOpen(false); bump(); }}
      />
    </div>
  );
}


// ---------------------------- Lists ----------------------------

function PendingList({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals/my-pending");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>;
  if (!rows.length) return <EmptyState label="No approvals waiting for your action." />;

  return (
    <div className="space-y-2">
      {rows.map(r => <RequestRow key={r.id} req={r} onOpen={onOpen} showSLA />)}
    </div>
  );
}

function MyRequestsList({ onOpen, onWithdrawn }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals/my-requests");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>;
  if (!rows.length) return <EmptyState label="You haven't submitted any approval requests yet." />;

  const withdraw = async (r) => {
    if (!window.confirm(`Withdraw this ${REQUEST_TYPE_LABEL[r.type] || r.type} request?`)) return;
    try {
      await api.post(`/approvals/${r.id}/withdraw`);
      toast.success("Request withdrawn");
      await load();
      onWithdrawn?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-2">
      {rows.map(r => (
        <RequestRow key={r.id} req={r} onOpen={onOpen} extraActions={
          r.status === "pending"
            ? <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={(e) => { e.stopPropagation(); withdraw(r); }} data-testid={`withdraw-${r.id}`}>Withdraw</Button>
            : null
        } />
      ))}
    </div>
  );
}

function HistoryList({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/approvals/my-history", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-end">
        <div className="space-y-1">
          <Label className="text-[10px] text-muted-foreground">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 w-40" data-testid="history-from" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] text-muted-foreground">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 w-40" data-testid="history-to" />
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="history-apply">Apply</Button>
      </div>

      {loading && <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>}
      {!loading && rows.length === 0 && <EmptyState label="No past decisions found in this range." />}
      {!loading && rows.map(r => <RequestRow key={r.id} req={r} onOpen={onOpen} />)}
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="p-12 text-center border rounded-md bg-muted/20">
      <ClipboardText size={32} className="mx-auto text-muted-foreground/50 mb-2" />
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}


// ---------------------------- Row + Detail ----------------------------

function RequestRow({ req, onOpen, showSLA, extraActions }) {
  const sla = showSLA && req.status === "pending" ? slaState(req) : null;
  const badge = STATUS_BADGE[req.status] || STATUS_BADGE.pending;
  return (
    <div
      className="border rounded-md p-3 hover:bg-muted/40 cursor-pointer transition-colors"
      onClick={() => onOpen(req)}
      data-testid={`request-row-${req.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-sm">{REQUEST_TYPE_LABEL[req.type] || req.type}</span>
        <Badge className={badge.c + " text-[10px]"}>{badge.label}</Badge>
        {sla?.kind === "breached" && (
          <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px] gap-1" data-testid={`sla-breached-${req.id}`}>
            <WarningCircle size={11} /> {sla.label}
          </Badge>
        )}
        {sla?.kind === "warning" && (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] gap-1" data-testid={`sla-warning-${req.id}`}>
            <HourglassMedium size={11} /> {sla.label}
          </Badge>
        )}
        {sla?.kind === "ok" && (
          <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px] gap-1">
            <Clock size={11} /> {sla.label}
          </Badge>
        )}
        <div className="ml-auto flex items-center gap-2">
          {extraActions}
          <Button size="sm" variant="outline">Open</Button>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-3">
        <span>Requester: <b>{req.requester_employee_number || ""} {req.requester_name}</b></span>
        <span>Submitted: {fmtDate(req.created_at)}</span>
        {req.department && <span>Dept: {req.department}</span>}
        {req.template_name ? <span>Template: <i>{req.template_name}</i></span> : <span className="text-amber-700">Default (admin) routing</span>}
      </div>
    </div>
  );
}


function RequestDetailDialog({ req, onClose, onActed, currentUserId }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setNote(""); }, [req]);
  if (!req) return null;

  const currentStep = req.resolved_steps?.[req.current_step_index];
  const canAct = req.status === "pending"
                 && (currentStep?.resolved_approver_ids || []).includes(currentUserId);
  const badge = STATUS_BADGE[req.status] || STATUS_BADGE.pending;

  const doAction = async (action) => {
    if (action === "reject" && !note.trim()) return toast.error("Please provide a reason for rejection.");
    setSaving(true);
    try {
      await api.post(`/approvals/${req.id}/${action}`, { note: note.trim() || null });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      onActed();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!req} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="request-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {REQUEST_TYPE_LABEL[req.type] || req.type}
            <Badge className={badge.c}>{badge.label}</Badge>
          </DialogTitle>
          <DialogDescription>
            Requester: <b>{req.requester_employee_number || ""} {req.requester_name}</b>
            {req.department ? ` · ${req.department}` : ""} · Submitted {fmtDate(req.created_at)}
          </DialogDescription>
        </DialogHeader>

        {req.template_fallback && (
          <div className="p-2 rounded border border-amber-300 bg-amber-50 text-[11px] text-amber-900" data-testid="fallback-notice">
            No template matched this request type; routed to Admin as default.
          </div>
        )}

        <section className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Request details</h4>
          <div className="border rounded p-3 bg-muted/10 text-sm">
            <PayloadView payload={req.payload || {}} />
          </div>
        </section>

        <section className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Approval flow</h4>
          <div className="border rounded divide-y">
            {req.resolved_steps.map((s, i) => {
              const isCurrent = i === req.current_step_index && req.status === "pending";
              const icon = s.outcome === "approved" ? <CheckCircle className="text-emerald-600" size={14} />
                         : s.outcome === "rejected" ? <XCircle className="text-rose-600" size={14} />
                         : s.outcome === "sla_breached" ? <WarningCircle className="text-rose-600" size={14} />
                         : s.outcome === "skipped_self" ? <ArrowsClockwise className="text-muted-foreground" size={14} />
                         : isCurrent ? <HourglassMedium className="text-amber-600" size={14} />
                         : <Clock className="text-muted-foreground" size={14} />;
              return (
                <div key={`step-${s.step_number}`} className={"p-2.5 text-xs " + (isCurrent ? "bg-amber-50" : "")}>
                  <div className="flex items-center gap-2">
                    {icon}
                    <span className="font-semibold">Step {s.step_number}</span>
                    <span className="text-muted-foreground">{s.approver_type.replace("_", " ")}</span>
                    {s.final_escalation && <Badge variant="outline" className="text-[9px]">Final escalation</Badge>}
                    <span className="ml-auto text-muted-foreground">SLA {s.sla_working_days}wd</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Approver(s): {s.resolved_approver_names?.join(", ") || <i>none resolved</i>}
                  </div>
                  {s.acted_by_name && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Actioned by <b>{s.acted_by_name}</b> — {fmtDate(s.acted_at)}
                      {s.note ? <span> · “{s.note}”</span> : null}
                    </div>
                  )}
                  {s.outcome === "sla_breached" && (
                    <div className="text-[11px] text-rose-700 mt-0.5">SLA breached at {fmtDate(s.breached_at)}</div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Audit trail</h4>
          <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1 bg-muted/10 text-[11px]">
            {(req.audit_trail || []).map((e, i) => (
              <div key={`${e.timestamp}-${e.action}-${i}`} className="flex items-start gap-2">
                <span className="text-muted-foreground font-mono w-32 shrink-0">{fmtDate(e.timestamp)}</span>
                <span className="font-medium capitalize">{e.action.replace("_", " ")}</span>
                <span className="text-muted-foreground">by {e.actor_name}{e.actor_employee_number ? ` (${e.actor_employee_number})` : ""}</span>
                {e.note && <span className="text-muted-foreground italic">— {e.note}</span>}
              </div>
            ))}
          </div>
        </section>

        {canAct && (
          <section className="space-y-2 border-t pt-3">
            <Label className="text-xs">Note (required for rejection)</Label>
            <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Optional for approval; required for rejection." data-testid="action-note" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => doAction("reject")} disabled={saving} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="reject-btn">
                <XCircle size={13} className="mr-1" /> Reject
              </Button>
              <Button onClick={() => doAction("approve")} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn">
                <CheckCircle size={13} className="mr-1" /> Approve
              </Button>
            </div>
          </section>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PayloadView({ payload }) {
  const entries = Object.entries(payload || {}).filter(([k]) => k !== "submission_note");
  const note = payload?.submission_note;
  return (
    <div className="space-y-1">
      {entries.length === 0 && !note && <div className="text-xs text-muted-foreground italic">No details provided.</div>}
      {entries.map(([k, v]) => (
        <div key={k} className="grid grid-cols-3 gap-2 text-xs">
          <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
          <span className="col-span-2 whitespace-pre-wrap">{String(v ?? "—")}</span>
        </div>
      ))}
      {note && (
        <div className="grid grid-cols-3 gap-2 text-xs">
          <span className="text-muted-foreground">Submission note</span>
          <span className="col-span-2 whitespace-pre-wrap italic">&ldquo;{note}&rdquo;</span>
        </div>
      )}
    </div>
  );
}


// ---------------------------- Generic Request Form ----------------------------

function GenericRequestDialog({ open, onClose, onSubmitted }) {
  const [type, setType] = useState("leave_request");
  const [saving, setSaving] = useState(false);
  // Shared
  const [submissionNote, setSubmissionNote] = useState("");
  // Generic
  const [subject, setSubject] = useState("");
  const [details, setDetails] = useState("");
  // Leave
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveTypeId, setLeaveTypeId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [half, setHalf] = useState("first");
  const [leaveReason, setLeaveReason] = useState("");
  // Attendance correction (admin-only user pick omitted; requester = current user)
  const [attDate, setAttDate] = useState("");
  const [attFromStatus, setAttFromStatus] = useState("present");
  const [attToStatus, setAttToStatus] = useState("absent");
  const [attNote, setAttNote] = useState("");
  // Profile update
  const [myEmp, setMyEmp] = useState(null);
  const [pfPhone, setPfPhone] = useState("");
  const [pfEmail, setPfEmail] = useState("");
  const [pfDesignation, setPfDesignation] = useState("");
  const [pfAddress, setPfAddress] = useState("");
  const [pfReason, setPfReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setType("leave_request"); setSubmissionNote(""); setSubject(""); setDetails("");
    setLeaveTypeId(""); setFromDate(""); setToDate(""); setIsHalfDay(false); setHalf("first"); setLeaveReason("");
    setAttDate(""); setAttFromStatus("present"); setAttToStatus("absent"); setAttNote("");
    setPfPhone(""); setPfEmail(""); setPfDesignation(""); setPfAddress(""); setPfReason("");
    (async () => {
      try {
        const [lt, meRes] = await Promise.all([
          api.get("/settings/leave-types").catch(() => ({ data: [] })),
          api.get("/approvals/my-requests").catch(() => ({ data: [] })),  // just to warm cookies
        ]);
        setLeaveTypes((lt.data || []).filter(t => t.active !== false));
        // Load own employee record for profile-update prefill.
        // /workforce/employees is admin-only, so try to find via the user context instead.
        try {
          const meEmp = await api.get("/auth/me");
          const emps = await api.get("/workforce/employees").catch(() => ({ data: [] }));
          const mine = (emps.data || []).find(e => e.user_id === meEmp.data.id);
          if (mine) {
            setMyEmp(mine);
            setPfPhone(mine.phone || "");
            setPfEmail(mine.email || "");
            setPfDesignation(mine.designation || "");
          }
        } catch { /* silent */ }
      } catch { /* silent */ }
    })();
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      if (type === "generic") {
        if (!subject.trim()) { toast.error("Subject is required"); setSaving(false); return; }
        const { data } = await api.post("/approvals/submit", {
          request_type: "generic",
          submission_note: submissionNote || null,
          payload: { subject: subject.trim(), details: details.trim() || null },
        });
        if (data.template_fallback) toast.warning("Submitted — no template; routed to Admin default.");
        else toast.success("Request submitted for approval");
      } else if (type === "leave_request") {
        if (!leaveTypeId) { toast.error("Choose a leave type"); setSaving(false); return; }
        if (!fromDate || !toDate) { toast.error("Provide from/to dates"); setSaving(false); return; }
        if (isHalfDay && fromDate !== toDate) { toast.error("Half-day leave must be a single date"); setSaving(false); return; }
        // Uses the legacy /leaves endpoint which now ALSO spawns an approval_request.
        await api.post("/leaves", {
          leave_type_id: leaveTypeId,
          from_date: fromDate, to_date: toDate,
          is_half_day: isHalfDay, half: isHalfDay ? half : null,
          reason: leaveReason || null,
        });
        toast.success("Leave request submitted for approval");
      } else if (type === "attendance_correction") {
        if (!attDate) { toast.error("Pick a date"); setSaving(false); return; }
        if (!attNote.trim()) { toast.error("A note is required for corrections"); setSaving(false); return; }
        // Direct engine submission (backend correction record is created
        // only on admin's Staff Attendance edit; for self-service we route
        // this as a plain approval_request that admin can act on and
        // manually apply.)
        const { data } = await api.post("/approvals/submit", {
          request_type: "attendance_correction",
          submission_note: attNote || null,
          payload: {
            date: attDate,
            from_status: attFromStatus,
            to_status: attToStatus,
            note: attNote,
          },
        });
        if (data.template_fallback) toast.warning("Submitted — no template; routed to Admin default.");
        else toast.success("Attendance correction submitted");
      } else if (type === "profile_update") {
        const changes = {};
        if (pfPhone.trim())       changes.phone       = pfPhone.trim();
        if (pfEmail.trim())       changes.email       = pfEmail.trim();
        if (pfDesignation.trim()) changes.designation = pfDesignation.trim();
        if (pfAddress.trim())     changes.personal_address = pfAddress.trim();
        if (Object.keys(changes).length === 0) { toast.error("Change at least one field"); setSaving(false); return; }
        const { data } = await api.post("/approvals/profile-update", {
          changes, reason: pfReason || null,
        });
        if (data._dropped_fields && data._dropped_fields.length) {
          toast.warning(`Submitted. These fields were dropped (not self-serve): ${data._dropped_fields.join(", ")}`);
        } else {
          toast.success("Profile update submitted for approval");
        }
      }
      onSubmitted();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="new-request-dialog">
        <DialogHeader>
          <DialogTitle>New approval request</DialogTitle>
          <DialogDescription className="text-xs">
            Submit a request. Routing follows the templates configured in Settings → Workflow &amp; Approvals.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Request type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="request-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="leave_request">Leave Request</SelectItem>
                <SelectItem value="attendance_correction">Attendance Correction</SelectItem>
                <SelectItem value="profile_update">Profile Update</SelectItem>
                <SelectItem value="generic">Generic Approval Request</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* -------- Leave form -------- */}
          {type === "leave_request" && (
            <div className="space-y-3 border rounded p-3 bg-muted/10" data-testid="form-leave">
              <div className="space-y-1">
                <Label className="text-xs">Leave type *</Label>
                <Select value={leaveTypeId} onValueChange={setLeaveTypeId}>
                  <SelectTrigger data-testid="leave-type-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>
                    {leaveTypes.length === 0 && <div className="p-2 text-xs text-muted-foreground">Configure leave types in Settings → Attendance & Leave first.</div>}
                    {leaveTypes.map(t => (<SelectItem key={t.id} value={t.id}>{t.name}{t.is_paid === false ? " (unpaid)" : ""}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">From *</Label>
                  <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} data-testid="leave-from" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To *</Label>
                  <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} data-testid="leave-to" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={isHalfDay} onChange={e => setIsHalfDay(e.target.checked)} data-testid="leave-half" />
                Half day
              </label>
              {isHalfDay && (
                <Select value={half} onValueChange={setHalf}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first">First half</SelectItem>
                    <SelectItem value="second">Second half</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <div className="space-y-1">
                <Label className="text-xs">Reason</Label>
                <Textarea rows={2} value={leaveReason} onChange={e => setLeaveReason(e.target.value)} data-testid="leave-reason" />
              </div>
            </div>
          )}

          {/* -------- Attendance Correction form -------- */}
          {type === "attendance_correction" && (
            <div className="space-y-3 border rounded p-3 bg-muted/10" data-testid="form-attcorr">
              <div className="space-y-1">
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={attDate} onChange={e => setAttDate(e.target.value)} data-testid="attcorr-date" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">From status</Label>
                  <Select value={attFromStatus} onValueChange={setAttFromStatus}>
                    <SelectTrigger data-testid="attcorr-from"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["present","absent","leave","half_day_first","half_day_second","wfh","field","late","early_exit"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To status</Label>
                  <Select value={attToStatus} onValueChange={setAttToStatus}>
                    <SelectTrigger data-testid="attcorr-to"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["present","absent","leave","half_day_first","half_day_second","wfh","field","late","early_exit"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Note / justification *</Label>
                <Textarea rows={2} value={attNote} onChange={e => setAttNote(e.target.value)} data-testid="attcorr-note" />
              </div>
            </div>
          )}

          {/* -------- Profile Update form -------- */}
          {type === "profile_update" && (
            <div className="space-y-3 border rounded p-3 bg-muted/10" data-testid="form-profile">
              {!myEmp && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Couldn&apos;t auto-locate your employee record. Admin can still submit on your behalf.
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Phone</Label>
                  <Input value={pfPhone} onChange={e => setPfPhone(e.target.value)} data-testid="pf-phone" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input value={pfEmail} onChange={e => setPfEmail(e.target.value)} data-testid="pf-email" />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Designation</Label>
                  <Input value={pfDesignation} onChange={e => setPfDesignation(e.target.value)} data-testid="pf-designation" />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Personal address</Label>
                  <Textarea rows={2} value={pfAddress} onChange={e => setPfAddress(e.target.value)} data-testid="pf-address" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Reason for update</Label>
                <Input value={pfReason} onChange={e => setPfReason(e.target.value)} data-testid="pf-reason" />
              </div>
              <div className="text-[10px] text-muted-foreground">
                Only phone, email, designation, and address are self-serve. Sensitive fields
                (compensation, statutory, employee number) require Admin edit.
              </div>
            </div>
          )}

          {/* -------- Generic form -------- */}
          {type === "generic" && (
            <div className="space-y-3 border rounded p-3 bg-muted/10" data-testid="form-generic">
              <div className="space-y-1">
                <Label className="text-xs">Subject *</Label>
                <Input value={subject} onChange={e => setSubject(e.target.value)} data-testid="request-subject" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Details</Label>
                <Textarea rows={3} value={details} onChange={e => setDetails(e.target.value)} data-testid="request-details" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Submission note (optional)</Label>
                <Input value={submissionNote} onChange={e => setSubmissionNote(e.target.value)} data-testid="request-note" />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="request-submit">
            <PaperPlaneTilt size={13} className="mr-1" /> {saving ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ---------------------------- Delegations Panel ----------------------------

function DelegationsPanel({ currentUserId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals/delegations");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async (r) => {
    if (!window.confirm(`Revoke delegation to ${r.delegate_name}?`)) return;
    try {
      await api.delete(`/approvals/delegations/${r.id}`);
      toast.success("Delegation revoked");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const given = rows.filter(r => r.delegator_user_id === currentUserId);
  const received = rows.filter(r => r.delegate_user_id === currentUserId);

  return (
    <div className="space-y-4">
      <div className="rounded-md border p-3 bg-primary/5 border-primary/20 text-xs flex items-start gap-2">
        <UserSwitch size={16} className="text-primary shrink-0 mt-0.5" />
        <div className="flex-1">
          <b>Delegation lets someone else act on your approvals</b> during a date range — perfect
          for vacations. Both you and the delegate can act. Delegate actions are audit-tagged.
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="delegation-new-btn">
          <Plus size={13} className="mr-1" /> New delegation
        </Button>
      </div>

      {loading && <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>}

      {!loading && (
        <>
          <section className="space-y-2">
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              Delegations you granted ({given.length})
            </h4>
            {given.length === 0 && (
              <EmptyState label="You haven't delegated to anyone yet." />
            )}
            {given.map(r => (
              <DelegationRow key={r.id} row={r} today={today} onRevoke={() => revoke(r)} data-testid={`delegation-given-${r.id}`} />
            ))}
          </section>

          <section className="space-y-2 pt-2">
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              Delegations you received ({received.length})
            </h4>
            {received.length === 0 && (
              <EmptyState label="No one has delegated to you." />
            )}
            {received.map(r => (
              <DelegationRow key={r.id} row={r} today={today} onRevoke={null} data-testid={`delegation-received-${r.id}`} />
            ))}
          </section>
        </>
      )}

      <NewDelegationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => { setCreateOpen(false); await load(); }}
      />
    </div>
  );
}


function DelegationRow({ row, today, onRevoke, ...rest }) {
  const active = row.active && row.from_date <= today && row.to_date >= today;
  const upcoming = row.active && row.from_date > today;
  const expired = row.to_date < today;
  const revoked = !row.active && !expired;
  const badge = active ? { c: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Active" }
               : upcoming ? { c: "bg-blue-100 text-blue-800 border-blue-200", label: "Upcoming" }
               : revoked  ? { c: "bg-slate-200 text-slate-700 border-slate-300", label: "Revoked" }
               :            { c: "bg-slate-100 text-slate-600 border-slate-200", label: "Expired" };
  return (
    <div className="border rounded-md p-3 flex items-center gap-3 hover:bg-muted/30" {...rest}>
      <UserSwitch size={16} className="text-primary shrink-0" />
      <div className="flex-1 text-sm">
        <div className="flex items-center gap-2">
          <b>{row.delegator_name}</b>
          <span className="text-muted-foreground">→</span>
          <b>{row.delegate_name}</b>
          <Badge className={badge.c + " text-[10px]"}>{badge.label}</Badge>
        </div>
        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
          <CalendarBlank size={11} /> {row.from_date} → {row.to_date}
          {row.note && <span className="ml-2 italic">&ldquo;{row.note}&rdquo;</span>}
        </div>
      </div>
      {onRevoke && (active || upcoming) && (
        <Button variant="ghost" size="sm" onClick={onRevoke} className="text-rose-600 hover:text-rose-700" data-testid={`revoke-delegation-${row.id}`}>
          <Trash size={13} className="mr-1" /> Revoke
        </Button>
      )}
    </div>
  );
}


function NewDelegationDialog({ open, onClose, onCreated }) {
  const [candidates, setCandidates] = useState([]);
  const [query, setQuery] = useState("");
  const [delegateId, setDelegateId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDelegateId(""); setFromDate(""); setToDate(""); setNote(""); setQuery("");
    (async () => {
      try {
        const { data } = await api.get("/approvals/delegations/candidates");
        setCandidates(data);
      } catch (e) { /* silent */ }
    })();
  }, [open]);

  useEffect(() => {
    if (!open || !query) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/approvals/delegations/candidates", { params: { q: query } });
        if (!cancelled) setCandidates(data);
      } catch { /* silent */ }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, open]);

  const submit = async () => {
    if (!delegateId) return toast.error("Choose a delegate");
    if (!fromDate || !toDate) return toast.error("Pick a date range");
    if (fromDate > toDate) return toast.error("End date must be on or after start date");
    setSaving(true);
    try {
      const { data } = await api.post("/approvals/delegations", {
        delegate_user_id: delegateId, from_date: fromDate, to_date: toDate, note: note || null,
      });
      if (data.overlap_warning_id) {
        toast.warning("Delegation created. Note: you already have an overlapping active delegation.");
      } else {
        toast.success("Delegation created");
      }
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const selected = candidates.find(c => c.id === delegateId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="delegation-dialog">
        <DialogHeader>
          <DialogTitle>New delegation</DialogTitle>
          <DialogDescription className="text-xs">
            Grant a peer the ability to act on your approvals during a specific date range.
            Delegation is additive — you can still act too if you check in.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Delegate to *</Label>
            {selected ? (
              <div className="flex items-center gap-2 border rounded p-2 bg-primary/5 text-sm">
                <UserSwitch size={14} className="text-primary" />
                <div className="flex-1">
                  <div>{selected.name}</div>
                  <div className="text-[10px] text-muted-foreground">{selected.email}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setDelegateId("")} className="h-6 text-[10px]">Clear</Button>
              </div>
            ) : (
              <>
                <Input placeholder="Search by name or email…" value={query} onChange={e => setQuery(e.target.value)} className="h-8" data-testid="delegate-search" />
                <div className="max-h-40 overflow-y-auto border rounded bg-white divide-y">
                  {candidates.length === 0 && (
                    <div className="p-2 text-[11px] text-muted-foreground">No candidates found.</div>
                  )}
                  {candidates.slice(0, 20).map(c => (
                    <button
                      key={c.id} type="button"
                      onClick={() => setDelegateId(c.id)}
                      className="w-full text-left px-2 py-1.5 hover:bg-primary/5 text-xs flex items-center gap-2"
                      data-testid={`delegate-option-${c.id}`}
                    >
                      <span className="font-medium">{c.name}</span>
                      <span className="text-muted-foreground">· {c.email}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">From *</Label>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} data-testid="delegation-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To *</Label>
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} data-testid="delegation-to" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Note (optional)</Label>
            <Input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Vacation coverage" data-testid="delegation-note" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="delegation-save">
            {saving ? "Creating…" : "Create delegation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
